import { Grid } from '@mui/material';
import { RoutalDropdownIcon, RoutalPalette, RoutalSwitch, RoutalToggle, VSpacer } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';

import { capacitorService } from '../../../../application/CapacitorService';
import { storageService } from '../../../../application/StorageService';
import { ModalSelect } from '../../../../components/ModalSelect';
import { SelectPill } from '../../../../components/SelectPill';
import { StorageKeys } from '../../../../constants/StorageKeys';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { MapsApplicationTypes } from '../../../../interfaces/MapsApplication';
import { ScanDeviceTypes } from '../../../../interfaces/Scanner';
import InputText from '../../../../react-components/InputText';
import { Text } from '../../../../react-components/Text';
import { usePermissions } from '../../hooks/usePermissions';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { useFetchMapsApplication } from '../../route/stops/stop/hooks/useFetchMapsApplication';
import { useUpdateProject } from '../../routes-list/hooks/useUpdateProject';

export const RoutePreferences = () => {
  const { data: vehicleDetails } = useRetrieveVehicle();
  const { isSoloDriver } = usePermissions();
  const { isLoading: loadingPreferences, run: updateProject } = useUpdateProject();

  const [isOpenModalMaps, setIsOpenModalMaps] = useState(false);
  const [isOpenModalScanner, setIsOpenModalScanner] = useState(false);

  const [scanDevice, setScanDevice] = useState<ScanDeviceTypes>(`camera`);
  const [preferredScanDevice, setPreferredScanDevice] = useState<ScanDeviceTypes>(scanDevice);
  const { mapsApplication, updateMapsApplication, isLoading } = useFetchMapsApplication();
  const [preferredMapsApplication, setPreferredMapsApplication] = useState<MapsApplicationTypes | undefined>(
    mapsApplication
  );

  const fetchStorageData = async () => {
    const resultScan = await storageService.getItem({ key: StorageKeys.DRIVERS_SCANNING_DEVICE });
    if (resultScan) {
      setScanDevice(resultScan as ScanDeviceTypes); // Show banner if not dismissed
      setPreferredScanDevice(resultScan as ScanDeviceTypes);
    }
  };

  useEffect(() => {
    fetchStorageData();
  }, []);

  const debouncedUpdateStopDuration = debounce((value: number) => {
    if (value && value >= 0) {
      updateProject({
        stop_duration: value * 60,
      });
    }
  }, 500);

  if (!vehicleDetails) return null;

  const project = vehicleDetails.project;

  const service_duration = project.service_duration !== undefined ? project.service_duration / 60 : 0;

  return (
    <>
      <Grid
        container
        direction="column"
        style={{
          display: `flex`,
          flexDirection: `column`,
          flexWrap: `nowrap`,
        }}
      >
        <Text variant="label" intlId="driver.stops.config.preferences.route" style={{ fontSize: `16px` }} />
        <VSpacer medium />
        <Text variant="label" intlId="driver.stops.config.preferences.maps_application" style={{ fontSize: `13px` }} />

        <VSpacer small />
        <SelectPill
          radialIcon={false}
          onClick={() => setIsOpenModalMaps(true)}
          containerStyle={{
            borderColor: RoutalPalette.neutral10,
            justifyContent: `space-between`,
            alignItems: `center`,
            cursor: `pointer`,
          }}
        >
          <Text
            style={{ width: `100%` }}
            intlId={isLoading ? `loading` : `driver.stops.config.preferences.maps_application.${mapsApplication}`}
          />
          <RoutalDropdownIcon style={{ transform: `rotate(-90deg)` }} />
        </SelectPill>

        {!isSoloDriver && project.tasks_enabled && (
          <>
            <VSpacer medium />

            <Text
              variant="label"
              intlId="driver.stops.config.preferences.scanning_device"
              style={{ fontSize: `13px` }}
            />

            <VSpacer small />

            <SelectPill
              radialIcon={false}
              onClick={() => setIsOpenModalScanner(true)}
              containerStyle={{
                borderColor: RoutalPalette.neutral10,
                justifyContent: `space-between`,
                alignItems: `center`,
                cursor: `pointer`,
              }}
            >
              <Text
                style={{ width: `100%` }}
                intlId={`driver.stops.config.preferences.scanning_device.${scanDevice}`}
              />
              <RoutalDropdownIcon style={{ transform: `rotate(-90deg)` }} />
            </SelectPill>
          </>
        )}
        {isSoloDriver && (
          <>
            <VSpacer medium />

            <Text variant="label" intlId="driver.stops.config.preferences.duration" style={{ fontSize: `13px` }} />

            <VSpacer small />

            <Grid item>
              <InputText
                placeholderIntlId="driver.preferences.duration"
                fieldValue={service_duration}
                onChange={(value: number) => {
                  debouncedUpdateStopDuration(value);
                }}
                disabled={loadingPreferences}
                hoverBGColor={RoutalPalette.neutral02n}
                bGColor={RoutalPalette.neutral02n}
                variant="outlined"
                type="number"
                inputContainerStyles={{ width: `100%`, border: `1px solid ${RoutalPalette.neutral10}` }}
                inputStyles={{
                  width: `100%`,
                  padding: `16.5px 14px`,
                }}
                suffix="min"
                suffixStyles={{
                  marginRight: `12px`,
                }}
              />
            </Grid>

            <VSpacer medium />

            <Text variant="label" intlId="driver.stops.config.preferences.timezone" style={{ fontSize: `13px` }} />

            <VSpacer small />

            <SelectPill
              radialIcon={false}
              containerStyle={{
                borderColor: RoutalPalette.neutral10,
                justifyContent: `space-between`,
                alignItems: `center`,
                cursor: `pointer`,
                backgroundColor: RoutalPalette.neutral00,
              }}
            >
              <Text>
                {project.time_zone ?? intl.formatMessage({ id: `driver.stops.config.preferences.timezone.ph` })}
              </Text>
            </SelectPill>

            <VSpacer medium />

            <Grid display={`flex`} flexDirection={`row`} alignItems={`center`} justifyContent={`space-between`}>
              <Text variant="label" intlId="driver.stops.config.preferences.units" style={{ fontSize: `13px` }} />

              <RoutalToggle
                onChange={(option) => {
                  if (!loadingPreferences) {
                    if (option === `left`) {
                      updateProject({
                        distance_unit: `km`,
                      });
                    }
                    if (option === `right`) {
                      updateProject({
                        distance_unit: `mi`,
                      });
                    }
                  }
                }}
                selectedBackground={RoutalPalette.primary00}
                value={project.units.distance === `km` ? `left` : `right`}
                containerStyles={{
                  width: `fit-content`,
                  backgroundColor: RoutalPalette.neutral05,
                  borderRadius: `5px`,
                  height: `40px`,
                }}
                elementStyles={{
                  borderRadius: `5px`,
                  width: `100px`,
                  maxWidth: `100px`,
                }}
                leftChild={
                  <Text
                    intlId={`kilometers`}
                    style={{
                      color: project.units.distance === `km` ? RoutalPalette.neutral02n : RoutalPalette.primary00,
                    }}
                  />
                }
                rightChild={
                  <Text
                    intlId={`miles`}
                    style={{
                      color: project.units.distance === `mi` ? RoutalPalette.neutral02n : RoutalPalette.primary00,
                    }}
                  />
                }
              />
            </Grid>

            <VSpacer medium />

            <Grid display={`flex`} flexDirection={`row`} alignItems={`center`} justifyContent={`space-between`}>
              <Text variant="label" intlId="driver.stops.config.preferences.traffic" style={{ fontSize: `13px` }} />
              <RoutalSwitch
                key="avoid_traffic"
                checked={project.use_traffic}
                onChange={(event: any) => {
                  updateProject({
                    use_traffic: event.target.checked,
                  });
                }}
              />
            </Grid>
            <VSpacer medium />
            <Grid display={`flex`} flexDirection={`row`} alignItems={`center`} justifyContent={`space-between`}>
              <Text
                variant="label"
                intlId="driver.stops.config.preferences.stop_grouping"
                style={{ fontSize: `13px` }}
              />
              <RoutalSwitch
                key="enable_stop_grouping"
                checked={project.stop_grouping}
                onChange={(event: any) => {
                  updateProject({
                    stop_grouping: event.target.checked,
                  });
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      <ModalSelect
        open={isOpenModalScanner}
        title={{ intlId: `drivers.preferences.modal.scan.title` }}
        subtitle={{ intlId: `drivers.preferences.modal.scan.subtitle` }}
        onClose={() => setIsOpenModalScanner(false)}
        selectors={[
          {
            selected: preferredScanDevice === `camera`,
            onClick: () => {
              setPreferredScanDevice(`camera`);
            },
            children: (
              <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.scanning_device.camera`} />
            ),
          },
          {
            selected: preferredScanDevice === `scanner`,
            onClick: () => {
              setPreferredScanDevice(`scanner`);
            },
            children: (
              <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.scanning_device.scanner`} />
            ),
          },
        ]}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              storageService
                .setItem({ key: StorageKeys.DRIVERS_SCANNING_DEVICE, item: preferredScanDevice })
                .then(() => {
                  setScanDevice(preferredScanDevice);
                  setIsOpenModalScanner(false);
                });
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            variant: `text`,
            onClick: () => {
              setPreferredScanDevice(scanDevice);
              setIsOpenModalScanner(false);
            },
            style: {
              border: `none`,
            },
          },
        ]}
      />
      <ModalSelect
        open={isOpenModalMaps}
        title={{ intlId: `drivers.preferences.modal.map.title` }}
        subtitle={{ intlId: `drivers.preferences.modal.map.subtitle` }}
        onClose={() => setIsOpenModalMaps(false)}
        selectors={[
          {
            selected: preferredMapsApplication === `google_maps`,
            onClick: () => {
              setPreferredMapsApplication(`google_maps`);
            },
            children: (
              <Text
                style={{ cursor: `pointer` }}
                intlId={`driver.stops.config.preferences.maps_application.google_maps`}
              />
            ),
          },
          {
            selected: preferredMapsApplication === `waze`,
            onClick: () => {
              setPreferredMapsApplication(`waze`);
            },
            children: (
              <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.maps_application.waze`} />
            ),
          },
          ...(capacitorService.isIOS() || capacitorService.isWeb()
            ? [
                {
                  selected: preferredMapsApplication === `apple_maps`,
                  onClick: () => {
                    setPreferredMapsApplication(`apple_maps`);
                  },
                  children: (
                    <Text
                      style={{ cursor: `pointer` }}
                      intlId={`driver.stops.config.preferences.maps_application.apple_maps`}
                    />
                  ),
                },
              ]
            : []),
        ]}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              if (preferredMapsApplication) updateMapsApplication(preferredMapsApplication);
              setIsOpenModalMaps(false);
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            variant: `text`,
            onClick: () => {
              if (mapsApplication) setPreferredMapsApplication(mapsApplication);
              setIsOpenModalMaps(false);
            },
            style: {
              border: `none`,
            },
          },
        ]}
      />
    </>
  );
};
